/**
 * TrackPreviewCard Component
 * Displays individual tracks inside expanded playlists with drag functionality
 */

import { Music, GripVertical } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { Track } from '../../api/types';

interface TrackPreviewCardProps {
  track: Track;
  playlistId: string;
  onSelect?: (track: Track) => void;
}

export function TrackPreviewCard({ track, playlistId, onSelect }: TrackPreviewCardProps) {
  // Setup draggable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `library-track-${playlistId}-${track.id}`,
    data: {
      type: 'external-track',
      track,
      source: 'library-playlist',
      playlistId,
    },
  });

  // Transform style
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  // Extract data
  const albumCover = track.album.images?.[track.album.images.length - 1]?.url;
  const artistNames = track.artists.map((a) => a.name).join(', ');

  const handleClick = () => {
    if (!isDragging) {
      onSelect?.(track);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        group relative flex items-center gap-2 p-2 rounded-md
        bg-zinc-900/30 border border-zinc-800/50
        hover:bg-zinc-800/50 hover:border-emerald-500/30
        transition-all cursor-pointer
        ${isDragging ? 'cursor-grabbing' : ''}
      `}
      onClick={handleClick}
    >
      {/* Album Art */}
      <div className="relative w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-zinc-800">
        {albumCover ? (
          <img src={albumCover} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music className="w-5 h-5 text-zinc-600" />
          </div>
        )}
      </div>

      {/* Track Info */}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-zinc-100 truncate group-hover:text-emerald-400 transition-colors">
          {track.name}
        </div>
        <div className="text-[10px] text-zinc-500 truncate">
          {artistNames}
        </div>
        {track.album.name && (
          <div className="text-[9px] text-zinc-600 truncate mt-0.5">
            {track.album.name}
          </div>
        )}
      </div>

      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity p-1"
        onClick={(e) => e.stopPropagation()}
        title="Drag to workbench"
      >
        <GripVertical className="w-4 h-4 text-zinc-500" />
      </div>
    </div>
  );
}
